import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Globe, Lock, Users, Film, Tv, Layers, Upload, Trash2, Image as ImageIcon, Grid3X3, Crop, ZoomIn, ZoomOut } from 'lucide-react';
import {
  useUpdateWatchlistMutation,
  useDeleteWatchlistMutation,
  useUploadWatchlistCoverMutation,
  type WatchlistMeta,
  type MediaFilter,
} from '../hooks/useListsQuery';
import { getPosterUrl } from '../utils/helpers';

export interface PosterCandidate {
  poster_path: string;
  title: string;
}

interface EditListModalProps {
  isOpen: boolean;
  onClose: () => void;
  watchlist: WatchlistMeta;
  onDeleted?: () => void;
  posterCandidates?: PosterCandidate[];
}

// Cover cropper aspect ratio: 2:3 (vertical poster)
const CROP_ASPECT = 2 / 3;
const CROP_OUTPUT_WIDTH = 800;
const CROP_OUTPUT_HEIGHT = 1200;

export function EditListModal({ isOpen, onClose, watchlist, onDeleted, posterCandidates = [] }: EditListModalProps) {
  // Form state - initialize from watchlist
  const [name, setName] = useState(watchlist.name);
  const [showPosterPicker, setShowPosterPicker] = useState(false);
  const [isSettingPoster, setIsSettingPoster] = useState(false);
  const [description, setDescription] = useState(watchlist.description || '');
  const [visibility, setVisibility] = useState<'public' | 'friends' | 'private'>(watchlist.visibility);
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>(watchlist.media_filter);
  const [error, setError] = useState<string | null>(null);

  // Cover upload state
  const [coverPreview, setCoverPreview] = useState<string | null>(watchlist.cover_url || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cover cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropPan, setCropPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSavingCrop, setIsSavingCrop] = useState(false);
  const cropImageRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Mutations
  const updateMutation = useUpdateWatchlistMutation();
  const deleteMutation = useDeleteWatchlistMutation();
  const uploadCoverMutation = useUploadWatchlistCoverMutation();

  // Track cover removal separately for UI feedback
  const [isRemovingCover, setIsRemovingCover] = useState(false);

  const loading = updateMutation.isPending || deleteMutation.isPending || uploadCoverMutation.isPending || isRemovingCover || isSettingPoster || isSavingCrop;

  // Reset form when watchlist changes
  useEffect(() => {
    setName(watchlist.name);
    setDescription(watchlist.description || '');
    setVisibility(watchlist.visibility);
    setMediaFilter(watchlist.media_filter);
    setCoverPreview(watchlist.cover_url || null);
    setError(null);
    setShowDeleteConfirm(false);
    setShowPosterPicker(false);
    setShowCropper(false);
    setCropZoom(1);
    setCropPan({ x: 0, y: 0 });
  }, [watchlist]);

  // Cropper drag handlers
  const handleCropMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropPan.x, y: e.clientY - cropPan.y });
  }, [cropPan]);

  const handleCropMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setCropPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleCropMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Save cropped image: render to canvas and upload
  const handleSaveCrop = async () => {
    if (!coverPreview || !cropImageRef.current) return;

    setIsSavingCrop(true);
    setError(null);

    try {
      const img = cropImageRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = CROP_OUTPUT_WIDTH;
      canvas.height = CROP_OUTPUT_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      // Calculate source dimensions based on zoom and pan
      const containerWidth = cropContainerRef.current?.clientWidth || 300;
      const containerHeight = containerWidth / CROP_ASPECT;

      // Scale factors
      const imgAspect = img.naturalWidth / img.naturalHeight;
      let drawWidth: number, drawHeight: number;

      if (imgAspect > CROP_ASPECT) {
        // Image is wider than crop area
        drawHeight = containerHeight * cropZoom;
        drawWidth = drawHeight * imgAspect;
      } else {
        // Image is taller than crop area
        drawWidth = containerWidth * cropZoom;
        drawHeight = drawWidth / imgAspect;
      }

      // Center offset plus pan
      const offsetX = (containerWidth - drawWidth) / 2 + cropPan.x;
      const offsetY = (containerHeight - drawHeight) / 2 + cropPan.y;

      // Scale to output canvas
      const scaleX = CROP_OUTPUT_WIDTH / containerWidth;
      const scaleY = CROP_OUTPUT_HEIGHT / containerHeight;

      ctx.drawImage(
        img,
        offsetX * scaleX,
        offsetY * scaleY,
        drawWidth * scaleX,
        drawHeight * scaleY
      );

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob'));
        }, 'image/jpeg', 0.9);
      });

      // Create file for upload
      const file = new File([blob], 'cover-adjusted.jpg', { type: 'image/jpeg' });

      // Upload using existing mutation
      const result = await uploadCoverMutation.mutateAsync({
        listId: watchlist.id,
        file,
      });

      setCoverPreview(result.url);
      setShowCropper(false);
      setCropZoom(1);
      setCropPan({ x: 0, y: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save adjusted cover');
    } finally {
      setIsSavingCrop(false);
    }
  };

  const openCropper = () => {
    setCropZoom(1);
    setCropPan({ x: 0, y: 0 });
    setShowCropper(true);
  };

  if (!isOpen) return null;

  const isDefault = watchlist.is_default;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isDefault && !name.trim()) {
      setError('List name is required');
      return;
    }

    setError(null);

    try {
      await updateMutation.mutateAsync({
        listId: watchlist.id,
        // Only update name if not default
        ...(isDefault ? {} : { name: name.trim() }),
        description: description.trim() || null,
        visibility,
        mediaFilter,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update list');
    }
  };

  const handleRemoveCover = async () => {
    setIsRemovingCover(true);
    setError(null);

    try {
      await updateMutation.mutateAsync({
        listId: watchlist.id,
        coverUrl: null, // Remove cover
      });
      setCoverPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove cover');
    } finally {
      setIsRemovingCover(false);
    }
  };

  const handleCoverChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      setError(null);
      const result = await uploadCoverMutation.mutateAsync({
        listId: watchlist.id,
        file,
      });
      setCoverPreview(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload cover');
      // Revert preview on error
      setCoverPreview(watchlist.cover_url || null);
    }
  };

  const handleSelectPoster = async (posterPath: string) => {
    setIsSettingPoster(true);
    setError(null);

    try {
      const coverUrl = getPosterUrl(posterPath, 'large');
      await updateMutation.mutateAsync({
        listId: watchlist.id,
        coverUrl,
      });
      setCoverPreview(coverUrl);
      setShowPosterPicker(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set cover');
    } finally {
      setIsSettingPoster(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ listId: watchlist.id });
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete list');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      />

      {/* Modal */}
      <div
        className="relative bg-[#0F1419] rounded-xl ring-1 ring-white/[0.06] w-full max-w-md shadow-2xl shadow-black/50 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0F1419] flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white">Edit List</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Cover Image (custom lists only) */}
          {!isDefault && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Cover Image
              </label>
              <div className="flex gap-4">
                {/* Cover preview (2:3 poster aspect) */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="relative w-[120px] aspect-[2/3] rounded-lg overflow-hidden cursor-pointer group shrink-0"
                >
                  {coverPreview ? (
                    <>
                      <img
                        src={coverPreview}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Upload size={24} className="text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-[#1A1F26] ring-1 ring-white/[0.08] flex flex-col items-center justify-center gap-2 hover:bg-[#252B33] transition-colors">
                      <ImageIcon size={24} className="text-slate-500" />
                      <span className="text-xs text-slate-500 text-center px-2">Click to upload</span>
                    </div>
                  )}
                  {(uploadCoverMutation.isPending || isRemovingCover) && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 size={24} className="text-white animate-spin" />
                    </div>
                  )}
                </div>
                {/* Upload info */}
                <div className="flex flex-col justify-center">
                  <p className="text-xs text-slate-500 mb-2">JPG, PNG, or GIF. Max 5MB.</p>
                  {coverPreview && (
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCropper();
                        }}
                        disabled={loading}
                        className="flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300 disabled:opacity-50"
                      >
                        <Crop size={12} />
                        Adjust cover
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCover();
                        }}
                        disabled={loading}
                        className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 text-left"
                      >
                        Remove cover
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverChange}
                className="hidden"
              />

              {/* Poster picker toggle */}
              {posterCandidates.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPosterPicker(!showPosterPicker)}
                  disabled={loading}
                  className="mt-2 flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 disabled:opacity-50"
                >
                  <Grid3X3 size={16} />
                  {showPosterPicker ? 'Hide posters' : 'Use a poster from this list'}
                </button>
              )}

              {/* Poster picker grid */}
              {showPosterPicker && posterCandidates.length > 0 && (
                <div className="mt-3 p-3 bg-[#1A1F26] rounded-lg ring-1 ring-white/[0.08]">
                  <p className="text-xs text-slate-500 mb-2">Select a poster to use as cover</p>
                  <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                    {posterCandidates.map((candidate, idx) => (
                      <button
                        key={`${candidate.poster_path}-${idx}`}
                        type="button"
                        onClick={() => handleSelectPoster(candidate.poster_path)}
                        disabled={isSettingPoster}
                        className="relative aspect-[2/3] rounded overflow-hidden ring-1 ring-white/[0.08] hover:ring-teal-500 hover:ring-2 transition-all disabled:opacity-50"
                        title={candidate.title}
                      >
                        <img
                          src={getPosterUrl(candidate.poster_path, 'small')}
                          alt={candidate.title}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  {isSettingPoster && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-400">
                      <Loader2 size={14} className="animate-spin" />
                      Setting cover...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Name (editable for custom lists only) */}
          <div>
            <label htmlFor="list-name" className="block text-sm font-medium text-slate-300 mb-1">
              List Name {!isDefault && '*'}
            </label>
            {isDefault ? (
              <div className="px-3 py-2 bg-[#1A1F26]/50 ring-1 ring-white/[0.04] rounded-lg text-slate-400">
                {watchlist.name}
                <p className="text-xs text-slate-500 mt-1">Default list names cannot be changed</p>
              </div>
            ) : (
              <input
                id="list-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Awesome List"
                maxLength={100}
                disabled={loading}
                className="w-full px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50"
              />
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="list-description" className="block text-sm font-medium text-slate-300 mb-1">
              Description
            </label>
            <textarea
              id="list-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this list about?"
              maxLength={500}
              rows={3}
              disabled={loading}
              className="w-full px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 resize-none"
            />
          </div>

          {/* Media Type */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              What's in this list?
            </label>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'movie'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="movie"
                  checked={mediaFilter === 'movie'}
                  onChange={() => setMediaFilter('movie')}
                  disabled={loading}
                  className="sr-only"
                />
                <Film size={18} className={mediaFilter === 'movie' ? 'text-teal-400' : 'text-slate-500'} />
                <p className={`font-medium ${mediaFilter === 'movie' ? 'text-white' : 'text-slate-300'}`}>Movies only</p>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'tv'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="tv"
                  checked={mediaFilter === 'tv'}
                  onChange={() => setMediaFilter('tv')}
                  disabled={loading}
                  className="sr-only"
                />
                <Tv size={18} className={mediaFilter === 'tv' ? 'text-teal-400' : 'text-slate-500'} />
                <p className={`font-medium ${mediaFilter === 'tv' ? 'text-white' : 'text-slate-300'}`}>TV Shows only</p>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                mediaFilter === 'mixed'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="mediaFilter"
                  value="mixed"
                  checked={mediaFilter === 'mixed'}
                  onChange={() => setMediaFilter('mixed')}
                  disabled={loading}
                  className="sr-only"
                />
                <Layers size={18} className={mediaFilter === 'mixed' ? 'text-teal-400' : 'text-slate-500'} />
                <p className={`font-medium ${mediaFilter === 'mixed' ? 'text-white' : 'text-slate-300'}`}>Movies & TV Shows</p>
              </label>
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Visibility
            </label>
            <div className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'private'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                  disabled={loading}
                  className="sr-only"
                />
                <Lock size={18} className={visibility === 'private' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'private' ? 'text-white' : 'text-slate-300'}`}>Private</p>
                  <p className="text-sm text-slate-500">Only you can see this list</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'friends'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="friends"
                  checked={visibility === 'friends'}
                  onChange={() => setVisibility('friends')}
                  disabled={loading}
                  className="sr-only"
                />
                <Users size={18} className={visibility === 'friends' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'friends' ? 'text-white' : 'text-slate-300'}`}>Friends</p>
                  <p className="text-sm text-slate-500">Only your followers can see this</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                visibility === 'public'
                  ? 'border-teal-500 bg-teal-500/10'
                  : 'border-white/[0.08] hover:border-white/[0.12]'
              }`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                  disabled={loading}
                  className="sr-only"
                />
                <Globe size={18} className={visibility === 'public' ? 'text-teal-400' : 'text-slate-500'} />
                <div>
                  <p className={`font-medium ${visibility === 'public' ? 'text-white' : 'text-slate-300'}`}>Public</p>
                  <p className="text-sm text-slate-500">Anyone can see and follow this list</p>
                </div>
              </label>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-slate-300 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isDefault && !name.trim())}
              className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>

          {/* Delete Section (custom lists only) */}
          {!isDefault && (
            <div className="pt-4 mt-4 border-t border-white/[0.06]">
              {showDeleteConfirm ? (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg space-y-3">
                  <p className="text-sm text-red-400">
                    Are you sure you want to delete "{watchlist.name}"? You can recover it from the Deleted Lists page.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={loading}
                      className="flex-1 px-3 py-2 text-sm text-slate-300 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={loading}
                      className="flex-1 px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                  className="w-full px-4 py-2 text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  Delete List
                </button>
              )}
            </div>
          )}
        </form>

        {/* Cropper Overlay */}
        {showCropper && coverPreview && (
          <div className="absolute inset-0 z-20 bg-[#0F1419] flex flex-col">
            {/* Cropper Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
              <h3 className="text-lg font-semibold text-white">Adjust Cover</h3>
              <button
                type="button"
                onClick={() => setShowCropper(false)}
                disabled={isSavingCrop}
                className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* Cropper Area */}
            <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
              <div
                ref={cropContainerRef}
                className="relative bg-black rounded-lg overflow-hidden cursor-move"
                style={{ width: '280px', height: '420px' }}
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                onMouseLeave={handleCropMouseUp}
              >
                {/* Image */}
                <img
                  ref={cropImageRef}
                  src={coverPreview}
                  alt="Crop preview"
                  className="absolute pointer-events-none select-none"
                  crossOrigin="anonymous"
                  style={{
                    transform: `translate(${cropPan.x}px, ${cropPan.y}px) scale(${cropZoom})`,
                    transformOrigin: 'center center',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  draggable={false}
                />
                {/* Crop guides */}
                <div className="absolute inset-0 pointer-events-none border-2 border-white/30 rounded-lg" />
                <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="border border-white/10" />
                  ))}
                </div>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="p-4 border-t border-white/[0.06]">
              <div className="flex items-center gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setCropZoom(Math.max(0.5, cropZoom - 0.1))}
                  disabled={isSavingCrop || cropZoom <= 0.5}
                  className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors disabled:opacity-50"
                >
                  <ZoomOut size={18} />
                </button>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={cropZoom}
                  onChange={(e) => setCropZoom(parseFloat(e.target.value))}
                  disabled={isSavingCrop}
                  className="flex-1 h-2 bg-[#1A1F26] rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setCropZoom(Math.min(3, cropZoom + 0.1))}
                  disabled={isSavingCrop || cropZoom >= 3}
                  className="p-2 text-slate-400 hover:text-white hover:bg-[#1A1F26] rounded-lg transition-colors disabled:opacity-50"
                >
                  <ZoomIn size={18} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCropper(false)}
                  disabled={isSavingCrop}
                  className="flex-1 px-4 py-2 text-slate-300 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCrop}
                  disabled={isSavingCrop}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSavingCrop ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
